import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import PlaceSearch from '../components/PlaceSearch';
import GoogleMap from '../components/GoogleMap';
import { formatRoleName, formatRoles } from '../utils/roleUtils';
import {
  createUser,
  getUsers,
  updateUser,
  deleteUser,
  revokeUserToken,
  User,
  getStaffCategories,
  uploadFile,
  checkIn,
  checkOut,
  getCurrentStatus,
  getTenant,
} from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Cropper from 'react-easy-crop';
import { formatDateDDMMYYYY, getMaxDateToday } from '../utils/dateUtils';

const UserManagement: React.FC = () => {
  const { user: currentUser, hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [users, setUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [userToRevoke, setUserToRevoke] = useState<{ id: string; name: string } | null>(null);
  const [revokeError, setRevokeError] = useState('');
  const [revokeSuccess, setRevokeSuccess] = useState('');
  const [roles, setRoles] = useState<Array<{ _id: string; metadata: { name: string }; spec: { permissions: any[] } }>>([]);
  const [permissions, setPermissions] = useState<Array<{ _id: string; metadata: { name: string }; spec: { permStr: string } }>>([]);
  const [staffCategories, setStaffCategories] = useState<Array<{ _id: string; metadata: { name: string; displayName?: string }; spec?: { tenantId?: string | null; description?: string } }>>([]);
  const [tenantEmail, setTenantEmail] = useState<string | null>(null); // Store tenant's email to identify original tenant admin
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    roles: [] as string[], // Changed to array for multiple roles
    staffCategory: '',
    status: 'active' as 'active' | 'inactive',
    // Personal Info
    avatar: '',
    dob: '',
    gender: '',
    guardianName: '',
    // Employment
    supervisorId: '',
    employeeId: '',
    isSupervisor: false,
    approval: 'unapproved' as 'approved' | 'unapproved',
    // Bank Details
    ifscCode: '',
    bankName: '',
    bankBranchName: '',
    accountNumber: '',
    // KYC
    aadhaarNumber: '',
    drivingLicenseNumber: '',
    drivingLicenseValidity: '',
    // Financial
    epfNumber: '',
    uanNumber: '',
    esicNumber: '',
    // Insurance
    insuranceNumber: '',
    mPin: '',
  });
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [customPermissions, setCustomPermissions] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showMPin, setShowMPin] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    staffCategory: '',
    avatar: '',
    aadhaarNumber: '',
    ifscCode: '',
    accountNumber: '',
    bankName: '',
    bankBranchName: '',
    mPin: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showAttendanceDrawer, setShowAttendanceDrawer] = useState(false);
  const [attendanceUser, setAttendanceUser] = useState<User | null>(null);
  const [attendanceCheckInLocation, setAttendanceCheckInLocation] = useState<{
    latitude: number;
    longitude: number;
    address?: string;
  } | null>(null);
  const [attendanceCheckOutLocation, setAttendanceCheckOutLocation] = useState<{
    latitude: number;
    longitude: number;
    address?: string;
  } | null>(null);
  const [attendanceStatus, setAttendanceStatus] = useState<'checkedIn' | 'checkedOut' | 'notCheckedIn' | null>(null);
  const [currentStatusCheckInTime, setCurrentStatusCheckInTime] = useState<number | null>(null);
  const [attendanceStatusLoading, setAttendanceStatusLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [attendanceCheckInTime, setAttendanceCheckInTime] = useState<string>('');
  const [attendanceCheckOutTime, setAttendanceCheckOutTime] = useState<string>('');
  const [attendanceAlsoCheckout, setAttendanceAlsoCheckout] = useState(false);
  const [todayAttendanceComplete, setTodayAttendanceComplete] = useState<{
    checkInTime: number;
    checkOutTime: number;
  } | null>(null);

  const attendanceDrawerDisabled = !!todayAttendanceComplete;

  /** Current local datetime in datetime-local input format (YYYY-MM-DDTHH:mm) */
  const getNowDatetimeLocal = () =>
    new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  /** Fetch hospital default location from tenant for attendance drawer */
  const fetchHospitalDefaultLocation = useCallback(async (): Promise<{
    latitude: number;
    longitude: number;
    address?: string;
  } | null> => {
    if (!currentUser?.tenantId) return null;
    try {
      const response = await getTenant(currentUser.tenantId);
      const tenant = response.data?.data;
      const hospital = tenant?.spec?.hospital;
      if (!hospital) return null;
      const lat = hospital.hospitalLocation?.latitude ?? hospital.latitude;
      const lng = hospital.hospitalLocation?.longitude ?? hospital.longitude;
      if (lat == null || lng == null) return null;
      const addressParts = [
        hospital.name,
        hospital.address,
        hospital.city,
        hospital.state,
        hospital.zipCode,
        hospital.country,
      ].filter(Boolean);
      return {
        latitude: Number(lat),
        longitude: Number(lng),
        address: addressParts.length ? addressParts.join(', ') : undefined,
      };
    } catch (err) {
      console.error('Failed to fetch hospital location for attendance', err);
      return null;
    }
  }, [currentUser?.tenantId]);

  useEffect(() => {
    fetchUsers(1);
    fetchRoles();
    fetchPermissions();
    fetchTenantInfo();
    fetchStaffCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Close Mark Attendance drawer with Escape key
  useEffect(() => {
    if (!showAttendanceDrawer) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !attendanceSubmitting) {
        e.preventDefault();
        setShowAttendanceDrawer(false);
        setAttendanceUser(null);
        setAttendanceCheckInLocation(null);
        setAttendanceCheckOutLocation(null);
        setAttendanceError('');
        setAttendanceStatus(null);
        setCurrentStatusCheckInTime(null);
        setTodayAttendanceComplete(null);
        setAttendanceCheckInTime('');
        setAttendanceCheckOutTime('');
        setAttendanceAlsoCheckout(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAttendanceDrawer, attendanceSubmitting]);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Refetch users when debounced search changes
  useEffect(() => {
    fetchUsers(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchUsers = async (page: number, size: number = pageSize) => {
    try {
      setLoading(true);
      const response = await getUsers(page, size, debouncedSearchTerm || undefined);
      const data = response.data.data || [];
      const pagination = response.data.pagination;

      setUsers(data);

      if (pagination) {
        setCurrentPage(pagination.currentPage || page);
        setTotalItems(pagination.totalItems || data.length);
        setHasNextPage(!!pagination.hasNextPage);
        setHasPreviousPage(!!pagination.hasPreviousPage);
      } else {
        // Fallback when backend doesn't return pagination metadata
        setCurrentPage(1);
        setTotalItems(data.length);
        setHasNextPage(false);
        setHasPreviousPage(false);
      }
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // Fetch all available roles (system roles + database roles)
      const response = await api.get('/roles/available');
      setRoles(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load roles:', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const allPermissions: any[] = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await api.get(
          `/permissions?page=${currentPage}&pageSize=${pageSize}`
        );
        const data = response.data.data || [];
        allPermissions.push(...data);

        // Check pagination metadata to see if there are more pages
        const pagination = response.data.pagination;
        hasNextPage = pagination?.hasNextPage || false;
        currentPage++;
      }

      // Sort permissions alphabetically by the permission string (permStr)
      allPermissions.sort((a, b) => {
        const permA = (a.spec?.permStr || '').toLowerCase();
        const permB = (b.spec?.permStr || '').toLowerCase();
        if (permA < permB) return -1;
        if (permA > permB) return 1;
        return 0;
      });

      setPermissions(allPermissions);
    } catch (err: any) {
      console.error('Failed to load permissions:', err);
    }
  };

  const fetchTenantInfo = async () => {
    try {
      // Only fetch tenant info if user is a tenant admin (has tenantId)
      if (currentUser?.tenantId) {
        const response = await api.get(`/tenants/${currentUser.tenantId}`);
        if (response.data.data?.spec?.email) {
          setTenantEmail(response.data.data.spec.email.toLowerCase());
        }
      }
    } catch (err: any) {
      console.error('Failed to load tenant info:', err);
    }
  };

  const fetchStaffCategories = async () => {
    try {
      const response = await getStaffCategories();
      setStaffCategories(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load staff categories:', err);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchUsers(currentPage, pageSize);
  };

  const handleOpenModal = (user?: User, viewMode: boolean = false) => {
    if (user) {
      setIsViewMode(viewMode);
      setEditingUser(user);

      // Get user roles (support both old single role and new roles array)
      const userRoles = user.spec?.roles || (user.spec?.role ? [user.spec.role] : []);

      // Find matching roles from database (case-insensitive)
      // Map user roles to the exact role names from the roles list
      const matchingRoles = userRoles
        .map(roleName => {
          const foundRole = roles.find(r =>
            r.metadata.name.toLowerCase() === roleName.toLowerCase()
          );
          return foundRole ? foundRole.metadata.name : roleName;
        });

      setFormData({
        name: user.metadata?.name || user.name || '',
        email: user.spec?.email || user.email || '',
        phone: user.spec?.phone || user.phone || '',
        password: '',
        roles: matchingRoles.length > 0 ? matchingRoles : userRoles, // Use role names from DB for dropdown
        staffCategory: user.spec?.staffCategory || '',
        status: user.spec?.status === 'active' ? 'active' : 'inactive',
        // Personal Info
        avatar: user.spec?.avatar || user.spec?.personalInfo?.avatar || '',
        dob: user.spec?.personalInfo?.dob ? new Date(user.spec.personalInfo.dob).toISOString().split('T')[0] : '',
        gender: user.spec?.personalInfo?.gender || '',
        guardianName: user.spec?.personalInfo?.guardianName || '',
        // Employment
        supervisorId: user.spec?.employment?.supervisorId || '',
        employeeId: user.spec?.employment?.employeeId || '',
        isSupervisor: user.spec?.employment?.isSupervisor || false,
        approval: user.spec?.employment?.approval || 'unapproved',
        // Bank Details
        ifscCode: user.spec?.bankDetails?.ifscCode || '',
        bankName: user.spec?.bankDetails?.bankName || '',
        bankBranchName: user.spec?.bankDetails?.bankBranchName || '',
        accountNumber: user.spec?.bankDetails?.accountNumber || '',
        // KYC
        aadhaarNumber: user.spec?.kyc?.aadhaarNumber || '',
        drivingLicenseNumber: user.spec?.kyc?.drivingLicenseNumber || '',
        drivingLicenseValidity: user.spec?.kyc?.drivingLicenseValidity || '',
        // Financial
        epfNumber: user.spec?.financial?.epfNumber || '',
        uanNumber: user.spec?.financial?.uanNumber || '',
        esicNumber: user.spec?.financial?.esicNumber || '',
        // Insurance
        insuranceNumber: user.spec?.insurance?.insuranceNumber || '',
        mPin: '', // Not returned by API for security
      });
      setShowMoreDetails(true); // Show more details when editing
      // For original tenant admin, clear permissions (they can't be modified)
      // Case-insensitive check
      const hasTenantAdminRole = userRoles.some(r => r.toLowerCase() === 'tenantadmin');
      const userEmail = user.spec?.email || user.email;
      const isOriginalTenantAdmin = tenantEmail && userEmail &&
        userEmail.toLowerCase() === tenantEmail;
      if (isOriginalTenantAdmin && hasTenantAdminRole) {
        setCustomPermissions([]);
      } else {
        setCustomPermissions(user.spec?.customPermissions || []);
      }
    } else {
      setIsViewMode(false);
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        roles: [],
        staffCategory: '',
        status: 'active',
        // Personal Info
        avatar: '',
        dob: '',
        gender: '',
        guardianName: '',
        // Employment
        supervisorId: '',
        employeeId: '',
        isSupervisor: false,
        approval: 'unapproved',
        // Bank Details
        ifscCode: '',
        bankName: '',
        bankBranchName: '',
        accountNumber: '',
        // KYC
        aadhaarNumber: '',
        drivingLicenseNumber: '',
        drivingLicenseValidity: '',
        // Financial
        epfNumber: '',
        uanNumber: '',
        esicNumber: '',
        // Insurance
        insuranceNumber: '',
        mPin: '',
      });
      setShowMoreDetails(false); // Hide more details when creating new
      setCustomPermissions([]);
    }
    setShowModal(true);
    setError('');
    setShowPassword(false);
    setShowMPin(false);
    setValidationErrors({
      name: '',
      email: '',
      phone: '',
      password: '',
      staffCategory: '',
      avatar: '',
      aadhaarNumber: '',
      ifscCode: '',
      accountNumber: '',
      bankName: '',
      bankBranchName: '',
      mPin: '',
    });
    setAvatarUploadError('');
  };

  const validateName = (name: string): string => {
    if (!name.trim()) return 'Name is required';
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return '';
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return editingUser ? '' : 'Password is required'; // Optional when editing
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const validateMPin = (mPin: string): string => {
    if (!mPin) return '';
    if (!/^[0-9]{4,6}$/.test(mPin)) {
      return 'MPIN must be 4 to 6 digits (numbers only)';
    }
    return '';
  };

  const validateAvatar = (avatar: string): string => {
    if (!avatar) return '';
    try {
      const url = new URL(avatar);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return 'Avatar URL must start with http or https';
      }
      return '';
    } catch {
      return 'Invalid avatar URL';
    }
  };

  const onCropComplete = useCallback((_: any, croppedArea: any) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous'); // Prevent CORS issues
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    // Enforce square output (round avatar) by cropping to the smallest side
    const size = Math.min(pixelCrop.width, pixelCrop.height);
    const sx = pixelCrop.x + (pixelCrop.width - size) / 2;
    const sy = pixelCrop.y + (pixelCrop.height - size) / 2;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas not supported');
    }
    ctx.drawImage(image, sx, sy, size, size, 0, 0, size, size);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create image blob'));
        }
      }, 'image/jpeg');
    });
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarUploadError('Please select an image file');
      return;
    }
    const maxBytes = 3 * 1024 * 1024;
    if (file.size > maxBytes) {
      const { formatFileSizeMB } = await import('../utils/imageCompression');
      setAvatarUploadError(
        `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (3 MB). Please choose a smaller file.`
      );
      return;
    }

    setAvatarUploadError('');
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setAvatarPreview(result);
      setAvatarFile(file);
      setCropperOpen(true);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    reader.onerror = () => setAvatarUploadError('Failed to read file');
    reader.readAsDataURL(file);
  };

  const handleClearAvatar = () => {
    setFormData((prev) => ({ ...prev, avatar: '' }));
    setAvatarUploadError('');
    setAvatarPreview('');
    setAvatarFile(null);
    setCropperOpen(false);
  };

  const handleCloseCropper = () => {
    setCropperOpen(false);
    setAvatarFile(null);
    setAvatarPreview('');
    setAvatarUploadError('');
  };

  const handleUploadCropped = async () => {
    if (!avatarPreview || !croppedAreaPixels) {
      setAvatarUploadError('Please select an area to crop');
      return;
    }
    setAvatarUploading(true);
    const run = async () => {
    try {
      const croppedBlob = await getCroppedImg(avatarPreview, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], avatarFile?.name || 'avatar.jpg', { type: 'image/jpeg' });

      // Compress the cropped image before uploading (avatars: 400x400, max 200KB)
      const { compressImage } = await import('../utils/imageCompression');
      const compressedFile = await compressImage(croppedFile, 400, 400, 0.8, 200);

      const uploadFormData = new FormData();
      uploadFormData.append('file', compressedFile);
      uploadFormData.append('folderPath', 'images/avatars');

      const response = await uploadFile(uploadFormData);
      const uploadedUrl = response.data?.data?.url;
      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, avatar: uploadedUrl }));
        setCropperOpen(false);
        setAvatarPreview('');
        setAvatarFile(null);
      } else {
        setAvatarUploadError('Upload succeeded but no URL returned.');
      }
    } catch (err: any) {
      setAvatarUploadError(err.response?.data?.message || err.message || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
    }
    };
    setTimeout(run, 0);
  };

  const validateAadhaar = (aadhaarNumber: string): string => {
    if (!aadhaarNumber) return '';
    const aadhaarRegex = /^[0-9]{12}$/;
    if (!aadhaarRegex.test(aadhaarNumber)) {
      return 'Aadhaar number must be exactly 12 digits';
    }
    return '';
  };

  const validateIfsc = (ifscCode: string): string => {
    if (!ifscCode) return '';
    const ifscRegex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;
    if (!ifscRegex.test(ifscCode)) {
      return 'Invalid IFSC code format (e.g., ABCD0XXXXXX)';
    }
    return '';
  };

  const validateAccountNumber = (accountNumber: string): string => {
    if (!accountNumber) return '';
    const accountRegex = /^[0-9]{6,18}$/;
    if (!accountRegex.test(accountNumber)) {
      return 'Account number must be 6-18 digits and cannot contain letters';
    }
    return '';
  };

  const validateBankName = (bankName: string): string => {
    if (!bankName) return '';
    const nameRegex = /^[A-Za-z\s.&'()-]+$/;
    if (!nameRegex.test(bankName)) {
      return 'Bank name cannot contain numbers';
    }
    return '';
  };

  const validateBankBranchName = (bankBranchName: string): string => {
    if (!bankBranchName) return '';
    const nameRegex = /^[A-Za-z\s.&'()-]+$/;
    if (!nameRegex.test(bankBranchName)) {
      return 'Branch name cannot contain numbers';
    }
    return '';
  };

  // Capitalize the first letter of each word in a name
  const capitalizeName = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const handleFieldChange = (field: string, value: string) => {
    let nextValue: string = value;

    // No normalization for name or guardianName
    /*
    if (field === 'name' || field === 'guardianName') {
      nextValue = normalizeName(value);
    }
    */

    setFormData({ ...formData, [field]: nextValue });

    // Validate on change
    if (field === 'name') {
      setValidationErrors({ ...validationErrors, name: validateName(value) });
    } else if (field === 'email') {
      setValidationErrors({ ...validationErrors, email: validateEmail(value) });
    } else if (field === 'phone') {
      const numeric = value.replace(/\D/g, '');
      setFormData((prev) => ({ ...prev, phone: numeric }));
      setValidationErrors({ ...validationErrors, phone: validatePhone(numeric) });
      return;
    } else if (field === 'password') {
      setValidationErrors({ ...validationErrors, password: validatePassword(value) });
    } else if (field === 'mPin') {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setFormData((prev) => ({ ...prev, mPin: digits }));
      setValidationErrors({ ...validationErrors, mPin: validateMPin(digits) });
      return;
    } else if (field === 'staffCategory') {
      setValidationErrors({
        ...validationErrors,
        staffCategory: value ? '' : 'Staff category is required',
      });
    } else if (field === 'aadhaarNumber') {
      setValidationErrors({ ...validationErrors, aadhaarNumber: validateAadhaar(value) });
    } else if (field === 'ifscCode') {
      setValidationErrors({ ...validationErrors, ifscCode: validateIfsc(value) });
    } else if (field === 'accountNumber') {
      setValidationErrors({ ...validationErrors, accountNumber: validateAccountNumber(value) });
    } else if (field === 'bankName') {
      setValidationErrors({ ...validationErrors, bankName: validateBankName(value) });
    } else if (field === 'bankBranchName') {
      setValidationErrors({ ...validationErrors, bankBranchName: validateBankBranchName(value) });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formSubmitting) return;
    setError('');
    setFormSubmitting(true);

    // PROTECTION: Only protect original tenant admin (email matches tenant's email)
    const editingUserRoles = (editingUser?.spec?.roles || editingUser?.roles || (editingUser?.spec?.role || editingUser?.role ? [editingUser.spec?.role || editingUser.role] : [])).filter((r): r is string => !!r);
    const hasTenantAdminRole = editingUserRoles.some(r => r.toLowerCase() === 'tenantadmin');
    const editingUserEmail = editingUser?.spec?.email || editingUser?.email;
    const isOriginalTenantAdmin = tenantEmail && editingUserEmail &&
      editingUserEmail.toLowerCase() === tenantEmail;

    // Only protect original tenant admin, not all tenant admins
    if (hasTenantAdminRole && isOriginalTenantAdmin && !currentUser?.isSystem) {
      // Check if tenantAdmin role is being removed (case-insensitive)
      const formHasTenantAdmin = formData.roles.some(r => r.toLowerCase() === 'tenantadmin');
      if (!formHasTenantAdmin) {
        setError('Cannot remove tenantAdmin role from the original tenant admin. This user is protected.');
        setFormSubmitting(false);
        return;
      }
      if (customPermissions.length > 0) {
        setError('Cannot modify permissions of the original tenant admin.');
        setFormSubmitting(false);
        return;
      }
    }

    // Validate all fields
    const nameError = validateName(formData.name);
    const staffCategoryError =
      !formData.staffCategory || !formData.staffCategory.trim()
        ? 'Staff category is required'
        : '';
    let emailError = validateEmail(formData.email);
    let phoneError = validatePhone(formData.phone);
    const passwordError = validatePassword(formData.password);
    const mPinError = validateMPin(formData.mPin);
    const aadhaarError = validateAadhaar(formData.aadhaarNumber);
    const ifscError = validateIfsc(formData.ifscCode);
    const accountNumberError = validateAccountNumber(formData.accountNumber);
    const bankNameError = validateBankName(formData.bankName);
    const bankBranchNameError = validateBankBranchName(formData.bankBranchName);
    const avatarError = validateAvatar(formData.avatar);

    // Check if at least one authentication method is provided
    if (!formData.email && !formData.phone) {
      const atLeastOneMessage = 'Please provide at least email or phone number ';
      // Surface the requirement on both fields
      emailError = atLeastOneMessage;
      phoneError = atLeastOneMessage;
      setError(atLeastOneMessage);
    }

    setValidationErrors({
      name: nameError,
      email: emailError,
      phone: phoneError,
      password: passwordError,
      staffCategory: staffCategoryError,
      avatar: avatarError,
      aadhaarNumber: aadhaarError,
      ifscCode: ifscError,
      accountNumber: accountNumberError,
      bankName: bankNameError,
      bankBranchName: bankBranchNameError,
      mPin: mPinError,
    });

    // Don't submit if there are validation errors
    if (
      nameError ||
      emailError ||
      phoneError ||
      passwordError ||
      staffCategoryError ||
      mPinError ||
      aadhaarError ||
      ifscError ||
      accountNumberError ||
      bankNameError ||
      bankBranchNameError ||
      avatarError
    ) {
      setFormSubmitting(false);
      return;
    }

    // Validate roles exist in available roles list
    if (formData.roles.length === 0) {
      setError('Please select at least one role');
      setFormSubmitting(false);
      return;
    }

    // Validate all selected roles exist in available roles (from backend)
    const invalidRoles = formData.roles.filter(roleName =>
      !roles.some(r => r.metadata.name.toLowerCase() === roleName.toLowerCase())
    );

    if (invalidRoles.length > 0) {
      setError(`Invalid roles: ${invalidRoles.join(', ')}. Please select valid roles from the dropdown.`);
      setFormSubmitting(false);
      return;
    }

    try {
      // Convert roles to lowercase to match backend enum
      // const rolesValue = formData.roles.map(r => r.toLowerCase()); COMMENTED BECAUSE NO ENUM IN BACKEND NOW
      const rolesValue = formData.roles;
      const data = {
        metadata: { name: capitalizeName(formData.name) },
        spec: {
          tenantId: currentUser?.tenantId,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          avatar: formData.avatar || undefined,
          ...(formData.password && { password: formData.password }),
          ...(formData.mPin && { mPin: formData.mPin }),
          // Only exclude roles and permissions if editing ORIGINAL tenant admin AND not system admin
          // For regular users, other tenant admins, or system admin editing original tenant admin, always include roles
          ...((!isOriginalTenantAdmin || currentUser?.isSystem) && {
            roles: rolesValue,
            customPermissions,
          }),
          staffCategory: formData.staffCategory,
          status: formData.status,
          personalInfo: {
            ...(formData.dob && { dob: new Date(formData.dob).getTime() }),
            ...(formData.gender && { gender: formData.gender }),
            ...(formData.guardianName && { guardianName: capitalizeName(formData.guardianName) }),
          },
          employment: {
            ...(formData.supervisorId && { supervisorId: formData.supervisorId }),
            ...(formData.employeeId && { employeeId: formData.employeeId }),
            isSupervisor: formData.isSupervisor,
            approval: formData.approval,
          },
          bankDetails: {
            ...(formData.ifscCode && { ifscCode: formData.ifscCode }),
            ...(formData.bankName && { bankName: formData.bankName }),
            ...(formData.bankBranchName && { bankBranchName: formData.bankBranchName }),
            ...(formData.accountNumber && { accountNumber: formData.accountNumber }),
          },
          kyc: {
            ...(formData.aadhaarNumber && { aadhaarNumber: formData.aadhaarNumber }),
            ...(formData.drivingLicenseNumber && { drivingLicenseNumber: formData.drivingLicenseNumber }),
            ...(formData.drivingLicenseValidity && { drivingLicenseValidity: formData.drivingLicenseValidity }),
          },
          financial: {
            ...(formData.epfNumber && { epfNumber: formData.epfNumber }),
            ...(formData.uanNumber && { uanNumber: formData.uanNumber }),
            ...(formData.esicNumber && { esicNumber: formData.esicNumber }),
          },
          insurance: {
            ...(formData.insuranceNumber && { insuranceNumber: formData.insuranceNumber }),
          },
        },
      };

      if (editingUser) {
        const userId = editingUser._id || editingUser.id;
        if (!userId) {
          setError('User ID is missing');
          setFormSubmitting(false);
          return;
        }
        await updateUser(userId, data);
      } else {
        await createUser(data);
      }

      setShowModal(false);
      setFormData({
        name: '', email: '', phone: '', password: '', roles: [], staffCategory: '',
        status: 'active',
        avatar: '', dob: '', gender: '', guardianName: '',
        supervisorId: '', employeeId: '', isSupervisor: false, approval: 'unapproved',
        ifscCode: '', bankName: '', bankBranchName: '', accountNumber: '',
        aadhaarNumber: '',
        drivingLicenseNumber: '',
        drivingLicenseValidity: '',
        epfNumber: '', uanNumber: '', esicNumber: '',
        insuranceNumber: '',
        mPin: '',
      });
      setCustomPermissions([]);
      setEditingUser(null);
      fetchUsers(currentPage); // Refresh the list after creating/updating
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = (user: User) => {
    const userId = user._id || user.id;
    const userName = user.metadata?.name || user.name || 'Unknown';
    if (!userId) {
      setError('User ID is missing');
      return;
    }
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteConfirm(true);
    setDeleteError('');
  };

  const handleRevokeToken = (user: User) => {
    const userId = user._id || user.id;
    const userName = user.metadata?.name || user.name || 'Unknown';
    if (!userId) {
      setError('User ID is missing');
      return;
    }
    setUserToRevoke({ id: userId, name: userName });
    setShowRevokeConfirm(true);
    setRevokeError('');
    setRevokeSuccess('');
  };

  const confirmRevokeToken = async () => {
    if (!userToRevoke) return;

    try {
      setRevokeError('');
      setRevokeSuccess('');
      await revokeUserToken(userToRevoke.id, 'Revoked by admin');
      setRevokeSuccess(`User "${userToRevoke.name}" has been logged out. They will be forced to login again on their next request.`);
      setShowRevokeConfirm(false);
      setUserToRevoke(null);
      setTimeout(() => {
        setRevokeSuccess('');
      }, 5000);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to revoke token';
      setRevokeError(errorMessage);
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      const nextPage =
        currentPage > 1 && users.length === 1 ? currentPage - 1 : currentPage;
      fetchUsers(nextPage); // Refresh the list after deletion
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete user');
      // Keep modal open to show error
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for permission UI
  const toggleCustomPermission = (perm: { id: string; name: string }) => {
    const rolePermissions = getRolePermissions();
    const isInRole = rolePermissions.includes(perm.name);

    // Prevent unchecking permissions that are in role permissions
    const exists = customPermissions.find((p) => p.name === perm.name);
    if (exists && isInRole) {
      // Don't allow unchecking if it's in role permissions
      return;
    }

    if (exists) {
      setCustomPermissions(customPermissions.filter((p) => p.name !== perm.name));
    } else {
      setCustomPermissions([...customPermissions, perm]);
    }
  };

  // Permission categories structure - categories are resources (booking, patient, etc.)
  interface PermissionCategory {
    id: string; // resource name (e.g., "booking")
    name: string; // capitalized resource name (e.g., "Booking")
    operations: {
      [operation: string]: Array<{ id: string; permStr: string }>; // operation name -> array of permission objects
    };
  }

  // Map permissions to categories based on resource type
  const getPermissionCategories = (): PermissionCategory[] => {
    const categoryMap: { [key: string]: PermissionCategory } = {};

    permissions.forEach((perm) => {
      const permStr = perm.spec.permStr;
      const [resource, operation] = permStr.split('.');

      if (!categoryMap[resource]) {
        let categoryDisplayName = (perm.metadata?.name || '').trim();
        if (categoryDisplayName) {
          const opToDisplaySuffix: Record<string, string> = {
            checkin: 'Check In',
            checkout: 'Check Out',
            lifecycle: 'Trip Lifecycle',
          };
          const opSuffix = opToDisplaySuffix[operation.toLowerCase()] ?? (operation.charAt(0).toUpperCase() + operation.slice(1));
          const escaped = opSuffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          categoryDisplayName = categoryDisplayName.replace(new RegExp('\\s*' + escaped + '\\s*$', 'i'), '').trim();
        }
        if (!categoryDisplayName) {
          categoryDisplayName = resource.charAt(0).toUpperCase() + resource.slice(1);
        }
        categoryDisplayName = categoryDisplayName.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
        categoryMap[resource] = {
          id: resource,
          name: categoryDisplayName,
          operations: {},
        };
      }

      if (!categoryMap[resource].operations[operation]) {
        categoryMap[resource].operations[operation] = [];
      }

      categoryMap[resource].operations[operation].push({ id: perm._id, permStr: permStr });
    });

    return Object.values(categoryMap)
      .filter((c) => c.name !== 'Permission' && c.name !== 'Tenant')
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleCategoryToggle = (categoryId: string, checked: boolean) => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;

    const rolePermissions = getRolePermissions();
    const allPerms: Array<{ id: string; name: string }> = [];
    Object.values(category.operations).forEach((permObjs) => {
      permObjs.forEach((perm) => {
        allPerms.push({ id: perm.id, name: perm.permStr });
      });
    });

    if (checked) {
      const newPerms = allPerms
        .filter((perm) => {
          const isInRole = rolePermissions.includes(perm.name);
          return !customPermissions.some((p) => p.name === perm.name) && !isInRole;
        })
        .map((perm) => ({ id: perm.id, name: perm.name }));
      setCustomPermissions([...customPermissions, ...newPerms]);
    } else {
      // Remove all permissions from this category that are not in roles
      setCustomPermissions(customPermissions.filter(
        (p) => !allPerms.some((perm) => perm.name === p.name && !rolePermissions.includes(p.name))
      ));
    }
  };

  const handleOperationToggle = (
    categoryId: string,
    operation: string,
    checked: boolean
  ) => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category || !category.operations[operation]) return;

    const rolePermissions = getRolePermissions();
    const permObjs = category.operations[operation] || [];

    if (checked) {
      const newPerms = permObjs
        .filter((perm) => {
          const isInRole = rolePermissions.includes(perm.permStr);
          return !customPermissions.some((p) => p.name === perm.permStr) && !isInRole;
        })
        .map((perm) => ({ id: perm.id, name: perm.permStr }));
      setCustomPermissions([...customPermissions, ...newPerms]);
    } else {
      // Remove permissions that are not in roles
      const permNames = permObjs.map((p) => p.permStr);
      setCustomPermissions(customPermissions.filter(
        (p) => !permNames.includes(p.name) || rolePermissions.includes(p.name)
      ));
    }
  };

  const isCategoryChecked = (categoryId: string): boolean => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return false;

    const allPermNames: string[] = [];
    Object.values(category.operations).forEach((permObjs) => {
      permObjs.forEach((perm) => {
        allPermNames.push(perm.permStr);
      });
    });

    return allPermNames.some((permName) =>
      customPermissions.some((p) => p.name === permName)
    );
  };

  const isOperationChecked = (
    categoryId: string,
    operation: string
  ): boolean => {
    const categories = getPermissionCategories();
    const category = categories.find((c) => c.id === categoryId);
    if (!category || !category.operations[operation]) return false;

    const permObjs = category.operations[operation] || [];
    const permNames = permObjs.map((p) => p.permStr);
    return permNames.length > 0 && permNames.every((permName) =>
      customPermissions.some((p) => p.name === permName)
    );
  };

  // Get color for operation toggle - each operation type has its own unique color
  const getOperationColor = (operation: string): string => {
    const colorMap: { [key: string]: string } = {
      'get': '#4caf50',        // green
      'list': '#2196f3',       // blue
      'create': '#ffc107',     // yellow/amber
      'update': '#ff9800',     // orange
      'delete': '#f44336',     // red
      'view': '#9c27b0',       // purple
      'read': '#00bcd4',       // cyan
      'checkin': '#8bc34a',    // light green
      'checkout': '#ff5722',   // deep orange
      'livemap': '#3f51b5',    // indigo
      'lifecycle': '#009688',  // teal
    };

    return colorMap[operation.toLowerCase()] || '#607d8b'; // default: blue grey
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for permission UI
  const toggleSelectAllPermissions = () => {
    const rolePermissions = getRolePermissions();
    const allPermissionNames = permissions.map(perm => perm.spec.permStr);
    const allSelected = allPermissionNames.every(permName =>
      customPermissions.some(p => p.name === permName)
    );

    if (allSelected) {
      // Deselect all, but keep permissions that are in roles
      const rolePerms = permissions
        .filter(perm => rolePermissions.includes(perm.spec.permStr))
        .map(perm => ({
          id: perm._id,
          name: perm.spec.permStr
        }));
      setCustomPermissions(rolePerms);
    } else {
      // Select all
      const allPerms = permissions.map(perm => ({
        id: perm._id,
        name: perm.spec.permStr
      }));
      setCustomPermissions(allPerms);
    }
  };

  // Get permissions that are already in the selected roles
  const getRolePermissions = (): string[] => {
    if (formData.roles.length === 0) return [];

    const allRolePermissions = new Set<string>();

    formData.roles.forEach(roleName => {
      // Case-insensitive role matching
      const selectedRole = roles.find(r =>
        r.metadata.name.toLowerCase() === roleName.toLowerCase()
      );
      if (selectedRole) {
        selectedRole.spec.permissions.forEach((p: any) => {
          allRolePermissions.add(p.name || p.permStr || p);
        });
      }
    });

    return Array.from(allRolePermissions);
  };


  // Get inherited permissions from roles (for display only)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for permission UI
  const getInheritedPermissions = () => {
    const rolePermissions = getRolePermissions();
    return permissions.filter(perm => rolePermissions.includes(perm.spec.permStr));
  };

  // Sync role permissions to customPermissions - automatically check permissions that are in roles
  React.useEffect(() => {
    if (formData.roles.length === 0 || permissions.length === 0) {
      // If no roles selected, don't auto-add anything
      return;
    }

    const rolePermissions = getRolePermissions();
    if (rolePermissions.length === 0) return;

    // Get all permission objects that match role permissions
    const rolePermObjects = permissions
      .filter(perm => rolePermissions.includes(perm.spec.permStr))
      .map(perm => ({
        id: perm._id,
        name: perm.spec.permStr
      }));

    // Get current custom permission names
    const existingPermNames = new Set(customPermissions.map(p => p.name));
    const rolePermNames = new Set(rolePermObjects.map(p => p.name));

    // Check if all role permissions are already in customPermissions
    const allRolePermsIncluded = rolePermNames.size > 0 &&
      Array.from(rolePermNames).every(name => existingPermNames.has(name));

    if (allRolePermsIncluded) {
      // All role permissions are already included, no update needed
      return;
    }

    // Add role permissions that aren't already in customPermissions
    const newCustomPerms = [...customPermissions];
    rolePermObjects.forEach(rolePerm => {
      if (!existingPermNames.has(rolePerm.name)) {
        newCustomPerms.push(rolePerm);
      }
    });

    setCustomPermissions(newCustomPerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.roles, roles, permissions]);

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Staff Management</h2>
        <p>Manage staff in your tenant</p>
      </div> */}

      {revokeSuccess && (
        <div className="success-message" style={{
          marginBottom: '20px',
          padding: '12px 16px',
          background: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          ✓ {revokeSuccess}
        </div>
      )}

      <div className="card" style={{ marginTop: '24px', height: "calc(100vh - 185px)" }}>
        <div className="card-header">
          {/* <h3 className="card-title">Staff</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('user.create') && (
              <button className="btn-add" onClick={() => handleOpenModal(undefined, false)}>
                + Add Staff
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading staff...</div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <h3>No staff yet</h3>
            <p>Create staff with custom permissions and roles</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 340px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Avatar</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Staff Category</th>
                    <th>Status</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Created</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user._id || user.id}
                      onClick={() => handleOpenModal(user, true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {(() => {
                          const avatarUrl = user.spec?.avatar || user.spec?.personalInfo?.avatar;
                          const initial = (user.metadata?.name || user.name || user.spec?.email || 'U').charAt(0).toUpperCase();
                          return (
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt="avatar"
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{ fontWeight: 600, color: '#111827' }}>{initial}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ minWidth: '200px', maxWidth: '230px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        {user.metadata?.name || user.name || '-'}
                      </td>
                      <td>{user.spec?.email || user.email || '-'}</td>
                      <td>{user.spec?.phone || user.phone || '-'}</td>
                      <td>
                        <span className="role-badge">
                          {formatRoles((user.spec?.roles ||
                            user.roles ||
                            (user.spec?.role || user.role
                              ? [user.spec?.role || user.role]
                              : [])
                          ).filter((r): r is string => !!r))}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const categoryName = user.spec?.staffCategory;
                          if (!categoryName) {
                            return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
                          }
                          const category = staffCategories.find(
                            c => c.metadata.name === categoryName || c.metadata.displayName === categoryName
                          );
                          return category ? (category.metadata.displayName || category.metadata.name) : categoryName;
                        })()}
                      </td>
                      <td>
                        <span className={`status-badge status-${user.spec?.status || 'active'}`}>
                          {user.spec?.status || 'active'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {user.metadata?.createdAt
                          ? formatDateDDMMYYYY(user.metadata.createdAt)
                          : '-'}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {(() => {
                            // Check if user has tenantAdmin role
                            const userRoles = (user.spec?.roles || user.roles || (user.spec?.role || user.role ? [user.spec?.role || user.role] : [])).filter((r): r is string => !!r);
                            const hasTenantAdminRole = userRoles.some(r => r.toLowerCase() === 'tenantadmin');

                            // Check if this is the original tenant admin (email matches tenant's email)
                            const userEmail = user.spec?.email || user.email;
                            const isOriginalTenantAdmin = tenantEmail && userEmail &&
                              userEmail.toLowerCase() === tenantEmail;

                            // Only protect original tenant admin (not all tenant admins)
                            // System admin can always edit
                            const isProtected = hasTenantAdminRole && isOriginalTenantAdmin && !currentUser?.isSystem;

                            return isProtected ? (
                              <span
                                className="btn-icon"
                                style={{
                                  opacity: 0.6,
                                  cursor: 'not-allowed',
                                  color: '#999',
                                  fontSize: '15px',
                                  padding: '6px 12px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  display: 'inline-block'
                                }}
                                title="Original tenant admin is protected. Only system admin can modify."
                              >
                                🔒 Protected
                              </span>
                            ) : (
                              <>
                                {/* View button (read-only drawer) */}
                                <button
                                  className="btn-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(user, true);
                                  }}
                                  title="View staff details"
                                  style={{
                                    marginRight: '8px',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #16a34a',
                                    background: '#16a34a',
                                    color: '#ffffff',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  View
                                </button>

                                <button
                                  className="btn-icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAttendanceUser(user);
                                    setAttendanceCheckInLocation(null);
                                    setAttendanceCheckOutLocation(null);
                                    setAttendanceError('');
                                    setAttendanceCheckInTime(getNowDatetimeLocal());
                                    setAttendanceCheckOutTime(getNowDatetimeLocal());
                                    setAttendanceAlsoCheckout(false);
                                    setTodayAttendanceComplete(null);
                                    setShowAttendanceDrawer(true);
                                    setAttendanceStatus(null);
                                    setAttendanceStatusLoading(true);
                                    const indiaDefault = {
                                      latitude: 20.5937,
                                      longitude: 78.9629,
                                      address: undefined as string | undefined,
                                    };
                                    const now = new Date();
                                    const startOfToday = new Date(
                                      now.getFullYear(),
                                      now.getMonth(),
                                      now.getDate(),
                                      0,
                                      0,
                                      0,
                                      0
                                    ).getTime();
                                    const endOfToday = new Date(
                                      now.getFullYear(),
                                      now.getMonth(),
                                      now.getDate(),
                                      23,
                                      59,
                                      59,
                                      999
                                    ).getTime();
                                    const userIdForStatus = user._id || user.id || '';
                                    getCurrentStatus(userIdForStatus)
                                      .then((res) => {
                                        const data = res.data.data;
                                        const status =
                                          (data.status as 'checkedIn' | 'checkedOut' | 'notCheckedIn') ||
                                          'notCheckedIn';
                                        setAttendanceStatus(status);
                                        setCurrentStatusCheckInTime(data.checkInTime ?? null);
                                        if (status === 'checkedIn' && data.checkInLocation) {
                                          setAttendanceCheckInLocation({
                                            latitude: data.checkInLocation.latitude,
                                            longitude: data.checkInLocation.longitude,
                                            address: data.checkInLocation.address,
                                          });
                                        }
                                        const setDefaultLocations = (loc: {
                                          latitude: number;
                                          longitude: number;
                                          address?: string;
                                        }) => {
                                          setAttendanceCheckOutLocation(loc);
                                          if (status !== 'checkedIn') {
                                            setAttendanceCheckInLocation(loc);
                                          }
                                        };
                                        if (currentUser?.tenantId) {
                                          fetchHospitalDefaultLocation().then((hospitalLoc) => {
                                            setDefaultLocations(hospitalLoc || indiaDefault);
                                          });
                                        } else {
                                          setDefaultLocations(indiaDefault);
                                        }
                                        return getCurrentStatus(
                                          userIdForStatus,
                                          startOfToday,
                                          endOfToday
                                        ).catch(() => null);
                                      })
                                      .then((todayRes) => {
                                        if (!todayRes?.data?.data) {
                                          setTodayAttendanceComplete(null);
                                          return;
                                        }
                                        const d = todayRes.data.data;
                                        if (
                                          d.status === 'checkedOut' &&
                                          d.checkInTime != null &&
                                          d.checkOutTime != null
                                        ) {
                                          setTodayAttendanceComplete({
                                            checkInTime: d.checkInTime,
                                            checkOutTime: d.checkOutTime,
                                          });
                                        } else {
                                          setTodayAttendanceComplete(null);
                                        }
                                      })
                                      .catch((err: any) => {
                                        // eslint-disable-next-line no-console
                                        console.error('Failed to fetch attendance status', err);
                                        setAttendanceError(
                                          err?.response?.data?.message ||
                                            'Failed to fetch current attendance status'
                                        );
                                        setAttendanceCheckInLocation(indiaDefault);
                                        setAttendanceCheckOutLocation(indiaDefault);
                                        setTodayAttendanceComplete(null);
                                      })
                                      .finally(() => {
                                        setAttendanceStatusLoading(false);
                                      });
                                  }}
                                  title="Mark attendance"
                                  style={{
                                    marginRight: '8px',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #0ea5e9',
                                    background: '#0ea5e9',
                                    color: '#ffffff',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Mark Attendance
                                </button>

                                {hasPermission('user.update') && (
                                  <button
                                    className="btn-icon btn-edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenModal(user, false);
                                    }}
                                    title="Edit staff permissions and details"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    Edit
                                  </button>
                                )}
                                {hasPermission('user.update') && (
                                  <button
                                    className="btn-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRevokeToken(user);
                                    }}
                                    title="Force logout - Revoke staff token"
                                    style={{
                                      background: '#ff9800',
                                      color: 'white',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '15px',
                                      marginRight: '8px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    Force Logout
                                  </button>
                                )}
                                {hasPermission('user.delete') && (
                                  <button
                                    className="btn-icon btn-delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(user);
                                    }}
                                    title="Delete staff"
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchUsers(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalItems
                    )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchUsers(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchUsers(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingUser
                  ? isViewMode
                    ? 'View Staff'
                    : 'Edit Staff'
                  : 'Create New Staff'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form" autoComplete="off">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Name <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Enter staff member's full name"
                    className={validationErrors.name ? 'input-error' : ''}
                    disabled={isViewMode}
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.name}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Avatar URL</label>
                  <div className="avatar-upload-row">
                    <input
                      type="url"
                      value={formData.avatar}
                      onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                      className={validationErrors.avatar ? 'input-error' : ''}
                      placeholder="https://example.com/avatar.jpg"
                      style={{ flex: '1 1 260px' }}
                      disabled={isViewMode}
                    />
                    {!formData.avatar && !isViewMode && (
                      <label className={`avatar-upload-button ${avatarUploading ? 'disabled' : ''}`} style={{ marginBottom: 0 }}>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleAvatarFileChange}
                          disabled={avatarUploading}
                        />
                        {avatarUploading ? (
                          <>
                            <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <span
                              role="img"
                              aria-label="upload"
                              style={{ marginRight: '8px', display: 'inline-block' }}
                            >
                              📤
                            </span>
                            Upload
                          </>
                        )}
                      </label>
                    )}
                    {formData.avatar && !isViewMode && (
                      <button
                        type="button"
                        onClick={handleClearAvatar}
                        className="btn-secondary avatar-clear-button"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {avatarUploading && (
                    <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Processing & uploading image...
                    </small>
                  )}
                  {(validationErrors.avatar || avatarUploadError) && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.avatar || avatarUploadError}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Optional: Upload an image or paste a URL. If left blank, initials will be shown. <span style={{ fontWeight: 600, color: '#374151' }}>Max size: 3MB.</span>
                  </small>
                  {formData.avatar && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: 120,
                          // height: 64,
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                          aspectRatio: '1 / 1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={formData.avatar}
                          alt="Avatar preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            aspectRatio: '1 / 1',
                            display: 'block'
                          }}
                        />
                      </div>
                      <span className="avatar-preview-text">{formData.avatar}</span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="user@example.com"
                    className={validationErrors.email ? 'input-error' : ''}
                    autoComplete="off"
                    disabled={isViewMode}
                  />
                  {validationErrors.email && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.email}
                    </small>
                  )}
                  {!validationErrors.email && !validationErrors.phone && (
                    <small style={{ color: '#9ca3af', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Optional: Provide email or phone (or both)
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="1234567890"
                    className={validationErrors.phone ? 'input-error' : ''}
                    maxLength={10}
                    disabled={isViewMode}
                  />
                  {validationErrors.phone && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px' }}>
                      {validationErrors.phone}
                    </small>
                  )}
                  {/* <small style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                    10-15 digits without spaces or special characters
                  </small> */}
                </div>

                <div className="form-group">
                  <label>
                    Staff Category <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.staffCategory}
                    onChange={(e) => handleFieldChange('staffCategory', e.target.value)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    className={validationErrors.staffCategory ? 'input-error' : ''}
                    disabled={isViewMode}
                  >
                    <option value="">Select a category</option>
                    {staffCategories.map((category) => {
                      const isSystemLevel = category.spec?.tenantId === null || category.spec?.tenantId === undefined;
                      return (
                        <option key={category._id} value={category.metadata.name}>
                          {category.metadata.displayName || category.metadata.name}
                          {isSystemLevel ? ' 🌍 (System)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  {validationErrors.staffCategory && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.staffCategory}
                    </small>
                  )}
                  {!validationErrors.staffCategory && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Required: Select a staff category for this user. Categories marked with 🌍 are system-level.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Password{!editingUser && <span className="required-star">*</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleFieldChange('password', e.target.value)}
                      placeholder={editingUser ? 'Leave blank to keep current password' : 'Enter secure password'}
                      className={validationErrors.password ? 'input-error' : ''}
                      style={{
                        paddingRight: '45px',
                      }}
                      autoComplete="new-password"
                      disabled={isViewMode}
                    />
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px',
                          color: '#666',
                          fontSize: '20px',
                        }}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    )}
                  </div>
                  {validationErrors.password && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.password}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Minimum 8 characters required
                  </small>
                </div>

                <div className="form-group">
                  <label>MPIN</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showMPin ? 'text' : 'password'}
                      inputMode="numeric"
                      autoComplete="off"
                      value={formData.mPin}
                      onChange={(e) => handleFieldChange('mPin', e.target.value)}
                      placeholder={editingUser ? 'Leave blank to keep current' : 'Optional. 4–6 digits'}
                      className={validationErrors.mPin ? 'input-error' : ''}
                      style={{ paddingRight: '45px' }}
                      maxLength={6}
                      disabled={isViewMode}
                    />
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => setShowMPin(!showMPin)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '5px',
                          color: '#666',
                          fontSize: '20px',
                        }}
                      >
                        {showMPin ? '👁️' : '👁️‍🗨️'}
                      </button>
                    )}
                  </div>
                  {validationErrors.mPin && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.mPin}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Optional. 4 to 6 digits (numbers only)
                  </small>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as 'active' | 'inactive',
                      })
                    }
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    disabled={isViewMode}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Inactive staff cannot log in
                  </small>
                </div>

                <div className="form-group">
                  <label>Approval Status</label>
                  <select
                    value={formData.approval}
                    onChange={(e) =>
                      setFormData({ ...formData, approval: e.target.value as 'approved' | 'unapproved' })
                    }
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    disabled={isViewMode}
                  >
                    <option value="unapproved">Unapproved</option>
                    <option value="approved">Approved</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Set the approval status for this staff member
                  </small>
                </div>

                {/* KYC Section (moved up from More Details) */}
                <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                  <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>
                    KYC Information
                  </h4>

                  <div className="form-group">
                    <label>Aadhaar Number</label>
                    <input
                      type="text"
                      value={formData.aadhaarNumber}
                      onChange={(e) =>
                        handleFieldChange('aadhaarNumber', e.target.value.replace(/\D/g, ''))
                      }
                      placeholder="12-digit Aadhaar number"
                      maxLength={12}
                      disabled={isViewMode}
                    />
                    {validationErrors.aadhaarNumber && (
                      <small
                        style={{
                          color: '#e74c3c',
                          fontSize: '14px',
                          marginTop: '4px',
                          display: 'block',
                        }}
                      >
                        {validationErrors.aadhaarNumber}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Driving Licence Number</label>
                    <input
                      type="text"
                      value={formData.drivingLicenseNumber}
                      onChange={(e) =>
                        handleFieldChange(
                          'drivingLicenseNumber',
                          e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                        )
                      }
                      placeholder="Enter driving licence number"
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="form-group">
                    <label>Driving Licence Validity</label>
                    <input
                      type="date"
                      value={formData.drivingLicenseValidity}
                      onChange={(e) => handleFieldChange('drivingLicenseValidity', e.target.value)}
                      placeholder="Select validity date"
                      // max={getMaxDateToday()}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Roles <span className="required-star">*</span>
                  </label>
                  {(() => {
                    // Check if tenantAdmin is selected in formData (current selection)
                    const isTenantAdminSelected = formData.roles.some(r => r.toLowerCase() === 'tenantadmin');
                    // Check if editing user has tenantAdmin role (for protection)
                    const editingUserRoles = (editingUser?.spec?.roles || editingUser?.roles || (editingUser?.spec?.role || editingUser?.role ? [editingUser.spec?.role || editingUser.role] : [])).filter((r): r is string => !!r);
                    const hasTenantAdminRole = editingUserRoles.some(r => r.toLowerCase() === 'tenantadmin');
                    // Check if this is the original tenant admin (email matches tenant's email)
                    const editingUserEmail = editingUser?.spec?.email || editingUser?.email;
                    const isOriginalTenantAdmin = !!(tenantEmail && editingUserEmail &&
                      editingUserEmail.toLowerCase() === tenantEmail);
                    // System admin can modify tenantAdmin role
                    const isSystemAdmin = !!currentUser?.isSystem;

                    const hasRolesError = formData.roles.length === 0;
                    return (
                      <>
                        <div
                          className={`roles-selection-container${hasRolesError ? ' input-error' : ''}`}
                          style={{
                            borderRadius: '6px',
                            padding: '12px',
                            minHeight: '120px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            cursor: isViewMode
                              ? 'default'
                              : (isTenantAdminSelected && !isSystemAdmin && isOriginalTenantAdmin) ||
                                (hasTenantAdminRole && !isSystemAdmin && isOriginalTenantAdmin)
                                ? 'not-allowed'
                                : 'default',
                          }}>
                          {roles.length === 0 ? (
                            <small className="empty-text" style={{ fontSize: '14px' }}>
                              No roles available. Please create roles first.
                            </small>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {roles.map((role) => {
                                const isChecked = formData.roles.includes(role.metadata.name);
                                const isTenantAdmin = role.metadata.name.toLowerCase() === 'tenantadmin';
                                // Disable if: 
                                // - Non-system admin trying to uncheck tenantAdmin from ORIGINAL tenant admin (protection)
                                // - tenantAdmin is currently selected and trying to select other roles (only for ORIGINAL tenant admin)
                                // BUT: System admin can always uncheck tenantAdmin and select other roles
                                // BUT: Other tenant admins (not original) can be modified - allow role changes
                                const isDisabled = !!(
                                  // Disable unchecking tenantAdmin checkbox for original tenant admin (non-system admin only)
                                  (!isSystemAdmin && isOriginalTenantAdmin && isTenantAdmin && isChecked) ||
                                  // Disable other roles when tenantAdmin is selected, but ONLY for original tenant admin
                                  (isTenantAdminSelected && !isTenantAdmin && !isSystemAdmin && isOriginalTenantAdmin)
                                );

                                return (
                                  <label
                                    key={role._id}
                                    className={`role-selection-item ${isChecked ? 'selected' : ''}`}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '8px 12px',
                                      borderRadius: '4px',
                                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                                      opacity: isDisabled ? 0.6 : 1,
                                      transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isDisabled && !isChecked && e.currentTarget) {
                                        e.currentTarget.classList.add('hover');
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isDisabled && !isChecked && e.currentTarget) {
                                        e.currentTarget.classList.remove('hover');
                                      }
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        if (isDisabled || isViewMode) return;

                                        // If selecting tenantAdmin, clear all other roles
                                        if (isTenantAdmin && !isChecked) {
                                          setFormData({ ...formData, roles: ['tenantAdmin'] });
                                          return;
                                        }

                                        // If unchecking tenantAdmin, allow other roles to be selected
                                        // System admin can always do this, tenant admin cannot (protection handled by isDisabled)
                                        if (isTenantAdmin && isChecked) {
                                          // Clear tenantAdmin, allow selecting other roles
                                          setFormData({ ...formData, roles: [] });
                                          return;
                                        }

                                        // If selecting another role when tenantAdmin is selected
                                        // System admin OR non-original tenant admin: Clear tenantAdmin and select the new role
                                        // Original tenant admin: This should be disabled (handled by isDisabled check)
                                        if (isTenantAdminSelected && !isChecked) {
                                          // Allow if system admin OR if it's not the original tenant admin
                                          if (isSystemAdmin || !isOriginalTenantAdmin) {
                                            setFormData({ ...formData, roles: [role.metadata.name] });
                                            return;
                                          }
                                          // For original tenant admin, this should not happen (disabled), but just in case
                                          return;
                                        }

                                        // Normal role toggle
                                        const newRoles = isChecked
                                          ? formData.roles.filter(r => r !== role.metadata.name)
                                          : [...formData.roles, role.metadata.name];
                                        setFormData({ ...formData, roles: newRoles });
                                      }}
                                      disabled={isDisabled || isViewMode}
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        cursor: isDisabled || isViewMode ? 'not-allowed' : 'pointer'
                                      }}
                                    />
                                    <span className="role-selection-label" style={{
                                      fontSize: '16px',
                                      fontWeight: isChecked ? '500' : '400'
                                    }}>
                                      {formatRoleName(role.metadata.name)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {isTenantAdminSelected && (
                          <small style={{ color: '#2196F3', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            ℹ️ Tenant admin has full access. No other roles or permissions needed.
                          </small>
                        )}
                        {isOriginalTenantAdmin && hasTenantAdminRole && !isTenantAdminSelected && !isSystemAdmin && (
                          <small style={{ color: '#ff9800', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            ⚠️ Original tenant admin role cannot be removed (protected)
                          </small>
                        )}
                        {hasTenantAdminRole && isSystemAdmin && !isTenantAdminSelected && (
                          <small style={{ color: '#ff9800', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            ⚠️ Warning: You are removing tenantAdmin role. This user will lose full tenant access.
                          </small>
                        )}
                        {formData.roles.length === 0 && (
                          <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            Please select at least one role
                          </small>
                        )}
                        <small className="helper-text" style={{ fontSize: '14px', marginTop: '4px', display: 'block' }}>
                          Select one or more roles for this user
                        </small>
                      </>
                    );
                  })()}
                </div>

                {(() => {
                  // Hide custom permissions if tenantAdmin is selected (either in formData or editing user)
                  const isTenantAdminSelected = formData.roles.some(r => r.toLowerCase() === 'tenantadmin');
                  const editingUserRoles = (editingUser?.spec?.roles || editingUser?.roles || (editingUser?.spec?.role || editingUser?.role ? [editingUser.spec?.role || editingUser.role] : [])).filter((r): r is string => !!r);
                  const hasTenantAdminRole = editingUserRoles.some(r => r.toLowerCase() === 'tenantadmin');
                  const editingUserEmail = editingUser?.spec?.email || editingUser?.email;
                  const isOriginalTenantAdmin = tenantEmail && editingUserEmail &&
                    editingUserEmail.toLowerCase() === tenantEmail;
                  const isSystemAdmin = currentUser?.isSystem;

                  // Hide if tenantAdmin is selected (but allow system admin to see it when downgrading)
                  // If system admin is downgrading tenantAdmin, show permissions section
                  if (isTenantAdminSelected) {
                    return null;
                  }

                  // If editing user is ORIGINAL tenant admin (not just any tenant admin), hide permissions
                  if (isOriginalTenantAdmin && hasTenantAdminRole && !isSystemAdmin) {
                    return null;
                  }

                  return (
                    <>
                      <div className="form-group">
                        <label>Custom Permissions (Additional)</label>
                        <small className="helper-text" style={{ fontSize: '15px', marginBottom: '8px', display: 'block' }}>
                          Grant additional permissions beyond what the roles provide
                        </small>
                        {permissions.length === 0 ? (
                          <p className="info-box" style={{
                            fontSize: '16px',
                            fontStyle: 'italic',
                            padding: '12px',
                            borderRadius: '6px',
                            color: isDark ? '#e0e0e0' : '#333',
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5',
                          }}>
                            No permissions available. Please create permissions first.
                          </p>
                        ) : (
                          <div
                            className="permission-list"
                            style={{
                              maxHeight: '400px',
                              overflowY: 'auto',
                              border: isDark ? '1px solid #444' : '1px solid #ddd',
                              borderRadius: '6px',
                              padding: '8px',
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                            }}
                          >
                            {getPermissionCategories().map((category) => {
                              const isCategorySelected = isCategoryChecked(category.id);
                              const operations = Object.keys(category.operations).sort();

                              return (
                                <div
                                  key={category.id}
                                  style={{
                                    marginBottom: '12px',
                                    border: isDark ? '1px solid #444' : '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isCategorySelected}
                                      onChange={(e) => handleCategoryToggle(category.id, e.target.checked)}
                                      style={{
                                        width: '18px',
                                        height: '18px',
                                        cursor: isViewMode ? 'default' : 'pointer',
                                        accentColor: '#2196f3',
                                      }}
                                      disabled={isViewMode}
                                    />
                                    <div
                                      style={{
                                        fontWeight: '700',
                                        fontSize: '18px',
                                        color: isDark ? '#e0e0e0' : '#333',
                                      }}
                                    >
                                      {category.name}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '32px',
                                      flexWrap: 'wrap',
                                      marginLeft: '30px',
                                    }}
                                  >
                                    {operations.map((operation) => {
                                      const isOpChecked = isOperationChecked(category.id, operation);
                                      const opColor = getOperationColor(operation);
                                      const operationLabel = operation.charAt(0).toUpperCase() + operation.slice(1);
                                      const rolePermissions = getRolePermissions();
                                      const permObjs = category.operations[operation] || [];
                                      const isInRole = permObjs.some((p) => rolePermissions.includes(p.permStr));

                                      return (
                                        <div
                                          key={operation}
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            cursor:
                                              isViewMode || (isInRole && isOpChecked)
                                                ? 'not-allowed'
                                                : 'pointer',
                                            opacity:
                                              isViewMode || (isInRole && isOpChecked)
                                                ? 0.7
                                                : 1,
                                          }}
                                          onClick={(e) => {
                                            if (isViewMode) return;
                                            if (!(isInRole && isOpChecked)) {
                                              e.preventDefault();
                                              handleOperationToggle(category.id, operation, !isOpChecked);
                                            }
                                          }}
                                          title={
                                            isViewMode
                                              ? ''
                                              : isInRole && isOpChecked
                                                ? 'This permission is already included in the selected role(s) and cannot be unchecked'
                                                : isInRole
                                                  ? 'This permission is already included in the selected role(s)'
                                                  : ''
                                          }
                                        >
                                          <span style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: isDark ? '#e0e0e0' : '#333',
                                          }}>
                                            {operationLabel}
                                            {isInRole && (
                                              <span style={{
                                                fontSize: '12px',
                                                color: '#4caf50',
                                                marginLeft: '4px',
                                                fontWeight: '500'
                                              }}>
                                                (in role)
                                              </span>
                                            )}
                                          </span>
                                          <div
                                            style={{
                                              width: '44px',
                                              height: '19px',
                                              borderRadius: '10px',
                                              background: isOpChecked ? opColor : (isDark ? '#555' : '#ccc'),
                                              position: 'relative',
                                              transition: 'background 0.3s',
                                              cursor: isInRole && isOpChecked ? 'not-allowed' : 'pointer',
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: '17px',
                                                height: '17px',
                                                borderRadius: '50%',
                                                background: isDark ? '#e0e0e0' : '#fff',
                                                position: 'absolute',
                                                top: '1px',
                                                left: isOpChecked ? '26px' : '1px',
                                                transition: 'left 0.3s',
                                                boxShadow: isDark ? '0 2px 4px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.2)',
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* {(() => {
                  // Hide inherited permissions if tenantAdmin is selected
                  const isTenantAdminSelected = formData.roles.some(r => r.toLowerCase() === 'tenantadmin');
                  if (isTenantAdminSelected) {
                    return null;
                  }
                  
                  return formData.roles.length > 0 && (
                    <div className="form-group">
                      <label>Inherited Permissions (from roles)</label>
                      <small className="helper-text" style={{ fontSize: '15px', marginBottom: '8px', display: 'block' }}>
                        These permissions are automatically granted by the selected roles (read-only)
                      </small>
                    {getInheritedPermissions().length === 0 ? (
                      <p className="empty-text" style={{ fontSize: '16px', fontStyle: 'italic', padding: '12px', borderRadius: '6px' }}>
                        No permissions inherited from this role
                      </p>
                    ) : (
                      <div className="permission-list" style={{ 
                        maxHeight: '200px', 
                        overflowY: 'auto'
                      }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                          gap: '8px'
                        }}>
                          {getInheritedPermissions().map((perm) => (
                            <div
                              key={perm._id}
                              className="inherited-permission-item"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                borderRadius: '6px'
                              }}
                            >
                              <span className="inherited-permission-label" style={{ fontSize: '16px', fontWeight: '500' }}>
                                ✓ {perm.metadata.name}
                              </span>
                              <code style={{ 
                                fontSize: '13px',
                                marginLeft: 'auto',
                                padding: '2px 6px',
                                borderRadius: '3px'
                              }}>
                                {perm.spec.permStr}
                              </code>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })()} */}

                {(() => {
                  const editingUserRoles = (editingUser?.spec?.roles || editingUser?.roles || (editingUser?.spec?.role || editingUser?.role ? [editingUser.spec?.role || editingUser.role] : [])).filter((r): r is string => !!r);
                  const hasTenantAdmin = editingUserRoles.some(r => r.toLowerCase() === 'tenantadmin');
                  // Only show this note for the ORIGINAL tenant admin (email matches tenant email)
                  const editingUserEmail = editingUser?.spec?.email || editingUser?.email;
                  const isOriginalTenantAdmin = !!(tenantEmail && editingUserEmail &&
                    editingUserEmail.toLowerCase() === tenantEmail);
                  const isSystemAdmin = !!currentUser?.isSystem;

                  // Only show note for original tenant admin (not system admin, as they can modify)
                  return hasTenantAdmin && isOriginalTenantAdmin && !isSystemAdmin && (
                    <div className="info-box" style={{
                      padding: '16px',
                      borderRadius: '8px',
                      marginTop: '16px'
                    }}>
                      <p style={{ fontSize: '16px', margin: 0 }}>
                        <strong>ℹ️ Note:</strong> Original tenant admin permissions are managed by the system and cannot be modified.
                        You can only update basic information (name, email, phone, password).
                      </p>
                    </div>
                  );
                })()}

                {/* Add More Details Section */}
                <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowMoreDetails(!showMoreDetails)}
                    className="add-details-button"
                    disabled={isViewMode}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #2196F3',
                      borderRadius: '6px',
                      cursor: isViewMode ? 'not-allowed' : 'pointer',
                      fontSize: '17px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.2s',
                      opacity: isViewMode ? 0.7 : 1,
                    }}
                  >
                    <span>{showMoreDetails ? '▼' : '▶'} Add More Details</span>
                    <span style={{ fontSize: '14px', opacity: 0.7 }}>
                      {showMoreDetails ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </button>

                  {showMoreDetails && (
                    <div style={{ marginTop: '20px' }}>
                      {/* Personal Info Section */}
                      <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                        <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>Personal Information</h4>

                        <div className="form-group">
                          <label>Date of Birth</label>
                          <input
                            type="date"
                            value={formData.dob}
                            max={getMaxDateToday()}
                            onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                            disabled={isViewMode}
                          />
                        </div>

                        <div className="form-group">
                          <label>Gender</label>
                          <select
                            value={formData.gender}
                            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            disabled={isViewMode}
                          >
                            <option value="">Select gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Guardian Name</label>
                          <input
                            type="text"
                            value={formData.guardianName}
                            onChange={(e) => handleFieldChange('guardianName', e.target.value)}
                            placeholder="Emergency contact name"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      {/* Employment Section */}
                      <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                        <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>Employment Details</h4>

                        <div className="form-group">
                          <label>Supervisor</label>
                          <select
                            value={formData.supervisorId}
                            onChange={(e) => setFormData({ ...formData, supervisorId: e.target.value })}
                            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            disabled={isViewMode}
                          >
                            <option value="">Select supervisor</option>
                            {users
                              .filter(
                                (u) =>
                                  u._id &&
                                  u._id !== (editingUser?._id || editingUser?.id) &&
                                  u.spec?.employment?.isSupervisor
                              )
                              .map((u) => (
                                <option key={u._id} value={u._id}>
                                  {u.metadata?.name || u.name || u._id}
                                </option>
                              ))}
                          </select>
                          <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                            Only staff marked as supervisor are listed here.
                          </small>
                        </div>

                        <div className="form-group">
                          <label>Employee ID</label>
                          <input
                            type="text"
                            value={formData.employeeId}
                            onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                            placeholder="Enter employee ID"
                            disabled={isViewMode}
                          />
                        </div>

                        <div className="form-group">
                          <label>Is Supervisor</label>
                          <select
                            value={formData.isSupervisor ? 'true' : 'false'}
                            onChange={(e) =>
                              setFormData({ ...formData, isSupervisor: e.target.value === 'true' })
                            }
                            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            disabled={isViewMode}
                          >
                            <option value="false">False</option>
                            <option value="true">True</option>
                          </select>
                        </div>
                      </div>

                      {/* Bank Details Section */}
                      <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                        <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>Bank Details</h4>

                        <div className="form-group">
                          <label>IFSC Code</label>
                          <input
                            type="text"
                            value={formData.ifscCode}
                            onChange={(e) => handleFieldChange('ifscCode', e.target.value.toUpperCase())}
                            placeholder="ABCD0123456"
                            maxLength={11}
                            disabled={isViewMode}
                          />
                          {validationErrors.ifscCode && (
                            <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                              {validationErrors.ifscCode}
                            </small>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Bank Name</label>
                          <input
                            type="text"
                            value={formData.bankName}
                            onChange={(e) => handleFieldChange('bankName', e.target.value)}
                            placeholder="Enter bank name"
                            disabled={isViewMode}
                          />
                          {validationErrors.bankName && (
                            <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                              {validationErrors.bankName}
                            </small>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Bank Branch Name</label>
                          <input
                            type="text"
                            value={formData.bankBranchName}
                            onChange={(e) => handleFieldChange('bankBranchName', e.target.value)}
                            placeholder="Enter branch name"
                            disabled={isViewMode}
                          />
                          {validationErrors.bankBranchName && (
                            <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                              {validationErrors.bankBranchName}
                            </small>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Account Number</label>
                          <input
                            type="text"
                            value={formData.accountNumber}
                            onChange={(e) => handleFieldChange('accountNumber', e.target.value)}
                            placeholder="Enter account number"
                            disabled={isViewMode}
                          />
                          {validationErrors.accountNumber && (
                            <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                              {validationErrors.accountNumber}
                            </small>
                          )}
                        </div>
                      </div>

                      {/* Financial Section */}
                      <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                        <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>Financial Information</h4>

                        <div className="form-group">
                          <label>EPF Number</label>
                          <input
                            type="text"
                            value={formData.epfNumber}
                            onChange={(e) => setFormData({ ...formData, epfNumber: e.target.value })}
                            placeholder="Enter EPF number"
                            disabled={isViewMode}
                          />
                        </div>

                        <div className="form-group">
                          <label>UAN Number</label>
                          <input
                            type="text"
                            value={formData.uanNumber}
                            onChange={(e) => setFormData({ ...formData, uanNumber: e.target.value })}
                            placeholder="Enter UAN number"
                            disabled={isViewMode}
                          />
                        </div>

                        <div className="form-group">
                          <label>ESIC Number</label>
                          <input
                            type="text"
                            value={formData.esicNumber}
                            onChange={(e) => setFormData({ ...formData, esicNumber: e.target.value })}
                            placeholder="Enter ESIC number"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      {/* Insurance Section */}
                      <div className="section-divider" style={{ marginTop: '24px', paddingTop: '24px' }}>
                        <h4 className="section-header" style={{ marginBottom: '16px', fontSize: '18px' }}>Insurance</h4>

                        <div className="form-group">
                          <label>Insurance Number</label>
                          <input
                            type="text"
                            value={formData.insuranceNumber}
                            onChange={(e) => setFormData({ ...formData, insuranceNumber: e.target.value })}
                            placeholder="Enter insurance number"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={formSubmitting}>
                  Cancel
                </button>
                {editingUser && isViewMode ? (
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={() => setIsViewMode(false)}
                  >
                    Edit Staff
                  </button>
                ) : (
                  <button type="submit" className="btn-submit" disabled={formSubmitting}>
                    {formSubmitting ? (
                      <>
                        <span className="loading-spinner btn-spinner" aria-hidden />
                        {editingUser ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingUser ? 'Update Staff' : 'Create Staff'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {showAttendanceDrawer && attendanceUser && (
        <div
          className="drawer-overlay"
          onClick={() => {
            if (!attendanceSubmitting) {
              setShowAttendanceDrawer(false);
              setAttendanceUser(null);
              setAttendanceCheckInLocation(null);
              setAttendanceCheckOutLocation(null);
              setAttendanceError('');
              setAttendanceStatus(null);
              setCurrentStatusCheckInTime(null);
              setTodayAttendanceComplete(null);
              setAttendanceCheckInTime('');
              setAttendanceCheckOutTime('');
              setAttendanceAlsoCheckout(false);
            }
          }}
        >
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '50vw',
              minWidth: '550px',
              maxWidth: '100%',
              maxHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="drawer-header">
              <h3 className="drawer-title">
                Mark Attendance – {attendanceUser.metadata?.name || attendanceUser.name}
              </h3>
              <button
                type="button"
                className="drawer-close"
                onClick={() => {
                  if (!attendanceSubmitting) {
                    setShowAttendanceDrawer(false);
                    setAttendanceUser(null);
                    setAttendanceCheckInLocation(null);
                    setAttendanceCheckOutLocation(null);
                    setAttendanceError('');
                    setAttendanceStatus(null);
                    setCurrentStatusCheckInTime(null);
                    setTodayAttendanceComplete(null);
                    setAttendanceCheckInTime('');
                    setAttendanceCheckOutTime('');
                    setAttendanceAlsoCheckout(false);
                  }
                }}
              >
                ×
              </button>
            </div>
            <div
              className="drawer-body"
              style={{
                padding: 16,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                color: isDark ? '#e2e8f0' : '#1e293b',
              }}
            >
              {attendanceError && (
                <div className="error-message" style={{ marginBottom: 12 }}>
                  {attendanceError}
                </div>
              )}

              {todayAttendanceComplete && (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 6,
                    marginBottom: 12,
                    background: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
                    border: isDark ? '1px solid rgba(239, 68, 68, 0.4)' : '1px solid #fecaca',
                    color: isDark ? '#fca5a5' : '#b91c1c',
                    fontSize: 14,
                  }}
                >
                  This user has already marked attendance for today. Check-in at{' '}
                  {new Date(todayAttendanceComplete.checkInTime).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                  , Check-out at{' '}
                  {new Date(todayAttendanceComplete.checkOutTime).toLocaleTimeString(undefined, {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </div>
              )}

              <div style={{ fontSize: 14, color: isDark ? '#94a3b8' : '#64748b' }}>
                {attendanceStatusLoading ? (
                  <span>Loading current attendance status…</span>
                ) : (
                  <>
                    <div>
                      <strong style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Current status:</strong>{' '}
                      {attendanceStatus === 'checkedIn' && (
                        <span className="status-badge status-checkedIn">Checked In</span>
                      )}
                      {attendanceStatus === 'checkedOut' && (
                        <span className="status-badge status-checkedOut">Checked Out</span>
                      )}
                      {attendanceStatus === 'notCheckedIn' && (
                        <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Not checked in</span>
                      )}
                    </div>
                    {attendanceStatus === 'checkedIn' && currentStatusCheckInTime != null && (
                      <div style={{ marginTop: 4, fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>
                        Checked in at{' '}
                        {new Date(currentStatusCheckInTime).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </div>
                    )}
                    <p style={{ marginTop: 4, fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>
                      {attendanceStatus === 'checkedIn'
                        ? 'You can perform an admin check-out for this user.'
                        : 'You can perform an admin check-in for this user.'}
                    </p>
                  </>
                )}
              </div>

              {/* Time inputs - default to now */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {attendanceStatus !== 'checkedIn' && (
                  <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 500,
                        marginBottom: 4,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Check-in time
                    </label>
                    <input
                      type="datetime-local"
                      value={attendanceCheckInTime}
                      onChange={(e) => setAttendanceCheckInTime(e.target.value)}
                      disabled={attendanceDrawerDisabled}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: isDark ? '1px solid #475569' : '1px solid #d1d5db',
                        background: isDark ? '#1e293b' : '#fff',
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        fontSize: 14,
                      }}
                    />
                  </div>
                )}
                {(attendanceStatus === 'checkedIn' || attendanceAlsoCheckout) && (
                  <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 500,
                        marginBottom: 4,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Check-out time
                    </label>
                    <input
                      type="datetime-local"
                      value={attendanceCheckOutTime}
                      onChange={(e) => setAttendanceCheckOutTime(e.target.value)}
                      disabled={attendanceDrawerDisabled}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: isDark ? '1px solid #475569' : '1px solid #d1d5db',
                        background: isDark ? '#1e293b' : '#fff',
                        color: isDark ? '#f1f5f9' : '#1e293b',
                        fontSize: 14,
                      }}
                    />
                  </div>
                )}
              </div>
              {attendanceStatus !== 'checkedIn' && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={attendanceAlsoCheckout}
                    onChange={(e) => setAttendanceAlsoCheckout(e.target.checked)}
                    disabled={attendanceDrawerDisabled}
                  />
                  <span>Also mark check-out now</span>
                </label>
              )}

              {/* Two maps in a row: Check-in location | Check-out location */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 16,
                  flex: 1,
                  minHeight: 0,
                  alignItems: 'stretch',
                }}
              >
                {(() => {
                  const fallbackCenter = { lat: 20.5937, lng: 78.9629 };

                  const showCheckIn = attendanceStatus !== 'checkedIn';
                  const showCheckOut = attendanceStatus === 'checkedIn' || attendanceAlsoCheckout;

                  return (
                    <>
                      {/* Check-in column */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          opacity: attendanceDrawerDisabled ? 0.6 : showCheckIn ? 1 : 0.6,
                          pointerEvents: attendanceDrawerDisabled ? 'none' : showCheckIn ? 'auto' : 'none',
                        }}
                      >
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            marginBottom: 4,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          }}
                        >
                          Check-in location
                        </label>
                        <div style={{ marginBottom: 6 }}>
                          <PlaceSearch
                            onPlaceSelect={(lat, lng, address) => {
                              setAttendanceCheckInLocation({ latitude: lat, longitude: lng, address });
                              setAttendanceError('');
                            }}
                            placeholder="Search check-in location…"
                            disabled={attendanceDrawerDisabled}
                          />
                        </div>
                        <div style={{ flex: 1, minHeight: 180 }}>
                          <GoogleMap
                            center={
                              attendanceCheckInLocation
                                ? { lat: attendanceCheckInLocation.latitude, lng: attendanceCheckInLocation.longitude }
                                : fallbackCenter
                            }
                            location={attendanceCheckInLocation}
                            onLocationChange={
                              attendanceDrawerDisabled
                                ? undefined
                                : (lat, lng) => {
                                    setAttendanceCheckInLocation((prev) => ({
                                      latitude: lat,
                                      longitude: lng,
                                      address: prev?.address,
                                    }));
                                    setAttendanceError('');
                                  }
                            }
                            height="100%"
                            markerIconUrl="/images/start_marker.png"
                          />
                        </div>
                      </div>
                      {/* Check-out column */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                          opacity: attendanceDrawerDisabled ? 0.6 : showCheckOut ? 1 : 0.6,
                          pointerEvents: attendanceDrawerDisabled ? 'none' : showCheckOut ? 'auto' : 'none',
                        }}
                      >
                        <label
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            marginBottom: 4,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          }}
                        >
                          Check-out location
                        </label>
                        <div style={{ marginBottom: 6 }}>
                          <PlaceSearch
                            onPlaceSelect={(lat, lng, address) => {
                              setAttendanceCheckOutLocation({ latitude: lat, longitude: lng, address });
                              setAttendanceError('');
                            }}
                            placeholder="Search check-out location…"
                            disabled={attendanceDrawerDisabled}
                          />
                        </div>
                        <div style={{ flex: 1, minHeight: 180 }}>
                          <GoogleMap
                            center={
                              attendanceCheckOutLocation
                                ? { lat: attendanceCheckOutLocation.latitude, lng: attendanceCheckOutLocation.longitude }
                                : fallbackCenter
                            }
                            location={attendanceCheckOutLocation}
                            onLocationChange={
                              attendanceDrawerDisabled
                                ? undefined
                                : (lat, lng) => {
                                    setAttendanceCheckOutLocation((prev) => ({
                                      latitude: lat,
                                      longitude: lng,
                                      address: prev?.address,
                                    }));
                                    setAttendanceError('');
                                  }
                            }
                            height="100%"
                            markerIconUrl="/images/end_marker.png"
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  if (!attendanceSubmitting) {
                    setShowAttendanceDrawer(false);
                    setAttendanceUser(null);
                    setAttendanceCheckInLocation(null);
                    setAttendanceCheckOutLocation(null);
                    setAttendanceError('');
                    setAttendanceStatus(null);
                    setCurrentStatusCheckInTime(null);
                    setTodayAttendanceComplete(null);
                    setAttendanceCheckInTime('');
                    setAttendanceCheckOutTime('');
                    setAttendanceAlsoCheckout(false);
                  }
                }}
                disabled={attendanceSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                disabled={
                  attendanceDrawerDisabled ||
                  attendanceStatusLoading ||
                  !attendanceStatus ||
                  (attendanceStatus === 'checkedIn' &&
                    (!attendanceCheckOutLocation || !attendanceCheckOutTime)) ||
                  (attendanceStatus !== 'checkedIn' &&
                    (!attendanceCheckInLocation ||
                      !attendanceCheckInTime ||
                      (attendanceAlsoCheckout &&
                        (!attendanceCheckOutLocation || !attendanceCheckOutTime)))) ||
                  attendanceSubmitting
                }
                onClick={async () => {
                  if (!attendanceUser || !attendanceStatus) return;
                  const userId = (attendanceUser._id || attendanceUser.id) as string;
                  const checkInLoc = attendanceCheckInLocation;
                  const checkOutLoc = attendanceCheckOutLocation;
                  if (attendanceStatus === 'checkedIn') {
                    if (!checkOutLoc || !attendanceCheckOutTime) return;
                  } else {
                    if (!checkInLoc || !attendanceCheckInTime) return;
                    if (attendanceAlsoCheckout && (!checkOutLoc || !attendanceCheckOutTime)) return;
                  }
                  try {
                    setAttendanceSubmitting(true);
                    setAttendanceError('');
                    if (attendanceStatus === 'checkedIn') {
                      const outTimeMs = new Date(attendanceCheckOutTime).getTime();
                      await checkOut(
                        userId,
                        checkOutLoc!.latitude,
                        checkOutLoc!.longitude,
                        undefined,
                        outTimeMs
                      );
                    } else {
                      const inTimeMs = new Date(attendanceCheckInTime).getTime();
                      await checkIn(
                        userId,
                        checkInLoc!.latitude,
                        checkInLoc!.longitude,
                        undefined,
                        inTimeMs
                      );
                      if (attendanceAlsoCheckout && checkOutLoc) {
                        const outTimeMs =
                          attendanceCheckOutTime && attendanceCheckOutTime.length
                            ? new Date(attendanceCheckOutTime).getTime()
                            : inTimeMs;
                        await checkOut(
                          userId,
                          checkOutLoc.latitude,
                          checkOutLoc.longitude,
                          undefined,
                          outTimeMs
                        );
                      }
                    }
                    await fetchUsers(currentPage, pageSize);
                    setShowAttendanceDrawer(false);
                    setAttendanceUser(null);
                    setAttendanceCheckInLocation(null);
                    setAttendanceCheckOutLocation(null);
                    setAttendanceStatus(null);
                    setCurrentStatusCheckInTime(null);
                    setTodayAttendanceComplete(null);
                    setAttendanceCheckInTime('');
                    setAttendanceCheckOutTime('');
                    setAttendanceAlsoCheckout(false);
                  } catch (err: any) {
                    console.error('Failed to mark attendance', err);
                    setAttendanceError(
                      err?.response?.data?.message || 'Failed to mark attendance for this user'
                    );
                  } finally {
                    setAttendanceSubmitting(false);
                  }
                }}
              >
                {attendanceSubmitting
                  ? 'Saving…'
                  : attendanceStatus === 'checkedIn'
                    ? 'Confirm Check-out'
                    : 'Confirm Check-in'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cropperOpen && (
        <div className="cropper-modal-overlay" onClick={handleCloseCropper}>
          <div className="cropper-modal" onClick={(e) => e.stopPropagation()}>
            <h4 style={{ marginBottom: '12px' }}>Crop Avatar</h4>
            <div className="cropper-container">
              <Cropper
                image={avatarPreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                cropShape="round"
                showGrid={false}
              />
            </div>
            <div className="cropper-controls">
              <label style={{ fontSize: '14px', color: '#6b7280' }}>Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </div>
            {avatarUploadError && (
              <div className="error-message" style={{ marginTop: '8px' }}>{avatarUploadError}</div>
            )}
            <div className="cropper-actions">
              <button type="button" className="btn-secondary" style={{ marginTop: 0 }} onClick={handleCloseCropper} disabled={avatarUploading}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleUploadCropped} disabled={avatarUploading}>
                {avatarUploading ? 'Uploading...' : 'Save & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Staff"
        message={
          deleteError
            ? deleteError
            : `Are you sure you want to delete the staff member "${userToDelete?.name}"? This action cannot be undone.`
        }
        confirmText={deleteError ? 'OK' : 'Yes, Delete'}
        cancelText={deleteError ? '' : 'Cancel'}
        type={deleteError ? 'danger' : 'danger'}
        onConfirm={() => {
          if (deleteError) {
            setShowDeleteConfirm(false);
            setUserToDelete(null);
            setDeleteError('');
          } else {
            confirmDelete();
          }
        }}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
          setDeleteError('');
        }}
      />

      <ConfirmModal
        isOpen={showRevokeConfirm}
        title="Force Logout Staff"
        message={
          revokeError
            ? revokeError
            : revokeSuccess
              ? revokeSuccess
              : `Are you sure you want to force logout staff member "${userToRevoke?.name}"? They will be required to login again on their next request.`
        }
        confirmText={revokeError || revokeSuccess ? 'OK' : 'Yes, Force Logout'}
        cancelText={revokeError || revokeSuccess ? '' : 'Cancel'}
        type={revokeError ? 'danger' : revokeSuccess ? 'info' : 'warning'}
        onConfirm={() => {
          if (revokeError || revokeSuccess) {
            setShowRevokeConfirm(false);
            setUserToRevoke(null);
            setRevokeError('');
            setRevokeSuccess('');
          } else {
            confirmRevokeToken();
          }
        }}
        onCancel={() => {
          setShowRevokeConfirm(false);
          setUserToRevoke(null);
          setRevokeError('');
          setRevokeSuccess('');
        }}
      />
    </DashboardLayout>
  );
};

export default UserManagement;

